/* eslint-disable react-refresh/only-export-components */
'use client';

import { CollectionCardTarget } from '@/components/Collections/CollectionCard/CollectionCard';
import { tabValues } from '@/components/Tabs/useActiveTab';
import { CardMediaType } from '@/hooks/cards/useCards';
import {
	minimumNativeVersion,
	NativePlatform,
	useCurrentNativeVersion,
	useNativePlatform,
} from '@/hooks/native/useIsNative';
import { Achievement, CalculatedAchievement } from '@/hooks/useAchievements';
import { Province } from '@/hooks/usePhysicalLocation';
import { FeedbackType } from '@/lib/feedback';
import isProduction from '@/lib/isProduction';
import {
	useAnalyticsPageTracker,
	useInitializeAnalytics,
	useInitializedAnalyticsEffect,
	useInitializedPageTrackerEffect,
} from '@ntrinternet/ntr-analytics/react';
import { MeasureClickTypeName } from '@ntrinternet/ntr-analytics/vanilla';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const Analytics = () => {
	const location = useLocation();
	const pathname = location.pathname;

	const nativePlatform = useNativePlatform();
	const nativeVersion = useCurrentNativeVersion();

	const { initialize, initializationState } = useInitializeAnalytics();
	const { setPageTrackerAndMeasurePageView } = useAnalyticsPageTracker();

	// Send app boot info when the page tracker is initialized
	const onInitializedPageTracker = useCallback(() => {
		measure(
			`app-boot-${nativePlatform ?? 'web'}-${nativeVersion ?? 'web'}`,
			'navigation',
		);
	}, [nativePlatform, nativeVersion]);
	useInitializedPageTrackerEffect(onInitializedPageTracker, true);

	// Initialize
	useEffect(() => {
		//
		if (
			!initializationState.isInitialized &&
			!initializationState.isInitializing
		) {
			initialize({
				isDebug: false,
				brand: isProduction() ? 'Hetklokhuis' : 'Hetklokhuis - dev',
				brandId: isProduction() ? 630426 : 630427,
				platform: 'site',
				platformVersion: nativeVersion ?? minimumNativeVersion,
				environment: undefined,
				disableNmoDam: false,
				enabledPlugins: new Set(
					isProduction()
						? ['govolte']
						: ['govolte', 'console'],
				),
			});
		}
	}, [initializationState, initialize, nativePlatform, nativeVersion]);

	const lastSentPagePathname = useRef('');
	const updatePageTracker = useCallback(() => {
		if (!initializationState.isInitialized) return;

		if (lastSentPagePathname.current === pathname) return;
		lastSentPagePathname.current = pathname;

		const { name, chapter1, chapter2, chapter3 } =
			getAnalyticsPageNameAndChapters(pathname);

		// Create the page tracker
		setPageTrackerAndMeasurePageView(name, {
			program: 'hetklokhuis',
			chapters: {
				1: chapter1,
				2: chapter2,
				3: chapter3,
			},
		});
	}, [
		initializationState.isInitialized,
		pathname,
		setPageTrackerAndMeasurePageView,
	]);

	// Run on every page change
	useEffect(updatePageTracker, [updatePageTracker]);
	// Also run when analytics have been initialized
	useInitializedAnalyticsEffect(updatePageTracker);

	return null;
};

const getAnalyticsPageNameAndChapters = (pathname: string) => {
	const pathWithoutFirstSlash = pathname.slice(1);
	const pathParts = pathWithoutFirstSlash.split('/');

	const isIndex = pathParts.length === 1;

	const [chapter2, chapter3, ...others] = pathWithoutFirstSlash.split('/');

	const name = isIndex
		? 'index'
		: others.length
			? others.join('/')
			: (chapter3 ?? chapter2 ?? '');

	return {
		pathParts,
		name,
		chapter1: 'soortsafari-subsite',
		chapter2:
			`${chapter2}`.length > 0 && chapter2 !== name
				? chapter2
				: undefined,
		chapter3:
			`${chapter3}`.length > 0 && chapter2 !== name
				? chapter3
				: undefined,
	};
};

type AnalyticsSpeciesInfo = `${number}-${string}`;
type AnalyticsCollectionInfo = `${number}-${string}`;

export const measure = (
	name:
		| `app-boot-${NativePlatform | 'web'}-${string}`
		| `tab-${(typeof tabValues)[number]}`
		| 'search-open-secondary'
		| 'search-open-camera'
		| 'search-open'
		| 'search-close'
		| `search-result-${AnalyticsSpeciesInfo}-${CollectionCardTarget}`
		| 'achievements-open'
		| 'achievements-close'
		| `achievements-achieved-${Achievement['id']}-${CalculatedAchievement['calculatedAchievedTimes']}-${CalculatedAchievement['calculatedTotalScore']}`
		| `calendar-species-${AnalyticsSpeciesInfo}`
		| `collection-species-${AnalyticsCollectionInfo}-${AnalyticsSpeciesInfo}`
		| `species-back-${AnalyticsSpeciesInfo}`
		| `species-search-${AnalyticsSpeciesInfo}`
		| `species-spotbutton-${AnalyticsSpeciesInfo}-${'camera' | 'recorder'}`
		| `species-gallery-open-${AnalyticsSpeciesInfo}`
		| `species-gallery-close-${AnalyticsSpeciesInfo}`
		| `species-gallery-share-${AnalyticsSpeciesInfo}`
		| `species-gallery-delete-${AnalyticsSpeciesInfo}`
		| `species-video-view-${AnalyticsSpeciesInfo}`
		| `species-manual-upload-photo-${AnalyticsSpeciesInfo}`
		| 'species-manual-upload-photo-error-connection'
		| `species-manual-upload-photo-identified-success-${string}-${AnalyticsSpeciesInfo}`
		| `species-manual-upload-photo-identified-error-${AnalyticsSpeciesInfo}`
		| `camera-upload-${AnalyticsSpeciesInfo}`
		| 'camera-identify-error-connection'
		| `camera-identified-success-${string}-${AnalyticsSpeciesInfo}`
		| `camera-identified-error-${AnalyticsSpeciesInfo}`
		| `camera-back-${AnalyticsSpeciesInfo}`
		| `camera-details-${AnalyticsSpeciesInfo}`
		| `camera-species-${AnalyticsSpeciesInfo}`
		| 'camera-zoom'
		| 'camera-focus'
		| `camera-error-identification-manual-${AnalyticsSpeciesInfo}`
		| `camera-error-retry-${AnalyticsSpeciesInfo}`
		| 'recorder-identify-error-connection'
		| `recorder-identified-success-${string}-${AnalyticsSpeciesInfo}`
		| `recorder-identified-error-${AnalyticsSpeciesInfo}`
		| `card-stored-${AnalyticsSpeciesInfo}-${Province}-${CardMediaType}`
		| `feedback-${FeedbackType}-stars-${number}-${string}`,
	type: MeasureClickTypeName = 'action',
) => {
	const { chapter1, chapter2, chapter3 } = getAnalyticsPageNameAndChapters(
		window.location.pathname,
	);

	window.ntrAnalytics?.measureClick(name, type, {
		chapters: {
			1: chapter1,
			2: chapter2,
			3: chapter3,
		},
	});
};

export default Analytics;
